import { useEffect, useRef, useCallback, useState } from 'react';
import { AppState, AppStateStatus } from 'react-native';
import websocketService from '../services/websocket';
import { useAuth } from '../contexts/AuthContext';
import { platformStorage } from '../utils/storage';
import logger from '../utils/logger';

interface TelemetryUpdate {
  deviceId: string;
  payload: {
    temperatureC?: number;
    humidityRh?: number;
    levelCm?: number;
    batteryV?: number;
    rssiDbm?: number;
    ts: string;
  };
}

/**
 * Hook للاتصال بـ WebSocket واستقبال التحديثات الفورية
 */
export function useWebSocket() {
  const { user } = useAuth();
  const [isConnected, setIsConnected] = useState(false);

  const connectWebSocket = useCallback(async () => {
    if (!user?.orgId) return;

    const token = await platformStorage.getItem('access_token');
    if (!token) return;

    // إذا الاتصال قائم ومستقر، لا داعي لإعادته
    if (websocketService.isConnected()) {
      setIsConnected(true);
      return;
    }

    // إعادة الاتصال (مع الحفاظ على المستمعين المسجّلة مسبقاً)
    logger.log('🔄 [useWebSocket] Reconnecting WebSocket on app foreground');
    websocketService.connect(user.orgId, token);
    // لا نقرأ isConnected() مباشرة - الاتصال غير متزامن
    // سيتم تحديث الحالة عبر subscribe('connect') أدناه
  }, [user?.orgId]);

  useEffect(() => {
    connectWebSocket();

    // الاستماع لأحداث الاتصال وانقطاعه مباشرةً من الخدمة
    const unsubConnect = websocketService.subscribe('connect', () => {
      setIsConnected(true);
    });
    const unsubDisconnect = websocketService.subscribe('disconnect', () => {
      setIsConnected(false);
    });

    // مراقبة حالة الاتصال كل 120 ثانية كحماية إضافية
    // 🛡️ Reduced from 30s to 120s — the 30s interval was unnecessarily
    //    aggressive and contributed to JS thread contention (crash F887917B).
    const statusInterval = setInterval(() => {
      const connected = websocketService.isConnected();
      setIsConnected(connected);

      // إذا انقطع الاتصال وSocket.IO توقف عن المحاولة، أعد الاتصال يدوياً
      if (!connected && user?.orgId) {
        connectWebSocket();
      }
    }, 120000); // 🛡️ Reduced from 30s to 120s

    // إعادة الاتصال عند عودة التطبيق للواجهة
    const subscription = AppState.addEventListener('change', (nextAppState: AppStateStatus) => {
      if (nextAppState === 'active') {
        logger.log('📱 [useWebSocket] App became active - checking WebSocket connection');
        connectWebSocket();
      }
    });

    return () => {
      unsubConnect();
      unsubDisconnect();
      clearInterval(statusInterval);
      subscription.remove();
    };
  }, [user?.orgId, connectWebSocket]);

  return { isConnected };
}

/**
 * Hook للاشتراك في تحديثات التيليمتري لجهاز معين
 */
export function useRealtimeTelemetry(
  deviceId: string,
  onUpdate: (data: TelemetryUpdate['payload']) => void,
) {
  const callbackRef = useRef(onUpdate);
  callbackRef.current = onUpdate;

  useEffect(() => {
    const unsubscribe = websocketService.subscribe('telemetry', (data: TelemetryUpdate) => {
      if (data.deviceId === deviceId) {
        logger.log('📊 Real-time update for device:', deviceId);
        callbackRef.current(data.payload);
      }
    });

    return unsubscribe;
  }, [deviceId]);
}

/**
 * Hook للاشتراك في الإنذارات
 */
export function useRealtimeAlarms(onAlarm: (alarm: any) => void) {
  const callbackRef = useRef(onAlarm);
  callbackRef.current = onAlarm;

  useEffect(() => {
    const unsubscribe = websocketService.subscribe('alarm', (alarm) => {
      callbackRef.current(alarm);
    });

    return unsubscribe;
  }, []);
}

/**
 * Hook للاشتراك في تحديثات حالة الأجهزة
 */
export function useRealtimeDeviceStatus(onStatusChange: (data: any) => void) {
  const callbackRef = useRef(onStatusChange);
  callbackRef.current = onStatusChange;

  useEffect(() => {
    const unsubscribe = websocketService.subscribe('device-status', (data) => {
      logger.log('📱 Device status update:', data);
      callbackRef.current(data);
    });

    return unsubscribe;
  }, []);
}

/**
 * Hook للتحديث الدوري (fallback إذا WebSocket غير متاح)
 */
export function usePolling(
  callback: () => Promise<void>,
  intervalMs: number = 10000,
  enabled: boolean = true,
) {
  const savedCallback = useRef(callback);
  savedCallback.current = callback;

  useEffect(() => {
    if (!enabled) return;

    const tick = () => {
      savedCallback.current().catch((error) => {
        logger.error('Polling error:', error);
      });
    };

    const intervalId = setInterval(tick, intervalMs);

    return () => clearInterval(intervalId);
  }, [intervalMs, enabled]);
}

/**
 * Hook يجمع بين WebSocket و Polling للتحديث المستمر
 */
export function useAutoRefresh(
  deviceId: string,
  fetchData: () => Promise<void>,
  options: {
    pollingInterval?: number;
    enablePolling?: boolean;
  } = {},
) {
  const { pollingInterval = 30000, enablePolling = true } = options;
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const [isLive, setIsLive] = useState(false);

  // الاشتراك في تحديثات WebSocket
  useRealtimeTelemetry(deviceId, (payload) => {
    setLastUpdate(new Date());
    setIsLive(true);
    // إعادة جلب البيانات عند استقبال تحديث
    fetchData();
  });

  // التحديث الدوري كـ fallback
  usePolling(
    async () => {
      await fetchData();
      setLastUpdate(new Date());
      setIsLive(false);
    },
    pollingInterval,
    enablePolling && !isLive,
  );

  return { lastUpdate, isLive };
}
